import React from 'react';
import { Amount, Box, Text } from '@razorpay/blade/components';
import { OvertimeSnapshotEntry } from '../EditPayroll/types';
import { formatOvertimeEntryQuantity } from '../EditPayroll/overtimeFormat';

type OvertimeBreakdownRow = {
  id: string;
  name: string;
  quantity: string;
  amount: number | null;
};

export interface OvertimeBreakdownTableProps {
  entries: OvertimeSnapshotEntry[];
  total: number | null;
  amountByCardId?: Map<string, number>;
  formulaCardNameById?: Map<string, string>;
}

const COMPONENT_COLUMN_WIDTH = '160px';

const OvertimeBreakdownTable = ({
  entries,
  total,
  amountByCardId,
  formulaCardNameById,
}: OvertimeBreakdownTableProps) => {
  const nodes: OvertimeBreakdownRow[] = entries.map((entry, index) => ({
    id: entry.formulaCardId ?? `ot-${index}`,
    name: entry.name ?? formulaCardNameById?.get(entry.formulaCardId ?? '') ?? 'Overtime',
    quantity: formatOvertimeEntryQuantity(entry),
    amount:
      entry.overrideAmount || entry.overrideAmount == 0
        ? entry.overrideAmount / 100
        : amountByCardId?.get(entry.formulaCardId ?? '') ?? null,
  }));

  return (
    <Box display="flex" flexDirection="column" gap="spacing.3" width="100%">
      {nodes.map((row) => (
        <Box
          key={row.id}
          display="grid"
          gridTemplateColumns={`${COMPONENT_COLUMN_WIDTH} 1fr auto`}
          gap="spacing.4"
          alignItems="baseline">
          <Text size="small" color="surface.text.gray.subtle">
            {row.name}
          </Text>
          <Text size="small" color="surface.text.gray.muted">
            {row.quantity || '-'}
          </Text>
          <Box textAlign="right">
            {row.amount !== null ? (
              <Amount value={row.amount} size="small" />
            ) : (
              <Text size="small" color="surface.text.gray.muted">
                -
              </Text>
            )}
          </Box>
        </Box>
      ))}
      <Box
        display="grid"
        gridTemplateColumns={`${COMPONENT_COLUMN_WIDTH} 1fr auto`}
        gap="spacing.4"
        alignItems="baseline"
        borderTopWidth="thin"
        borderTopColor="surface.border.gray.muted"
        paddingTop="spacing.3">
        <Text size="small" weight="semibold" color="surface.text.gray.subtle">
          Total
        </Text>
        <Box />
        <Box textAlign="right">
          {total !== null ? (
            <Amount value={total} size="small" weight="semibold" />
          ) : (
            <Text size="small" color="surface.text.gray.muted">
              -
            </Text>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export { OvertimeBreakdownTable };
